import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import { setSentryUserContext, clearSentryUserContext } from '@/config/sentry';
import { authApi } from '@/services/api/auth';
import { usersApi } from '@/services/api/users';
import { cleanupGlobalTimer } from '@/hooks/system/useGlobalTimelog.ts';
import { cleanupPatientTimer } from '@/hooks/patient/usePatientDetailsTimer.ts';
import { cleanupGlobalNotifications } from '@/hooks/system/useGlobalNotifications.ts';
import { cleanupHBoxCall, initializeHBoxCallGlobally } from '@/components/CallHandler/hooks/useHBoxCall';
import { transferWebSocketService } from '@/services/api/notifications/transferWebSocketService';
import { initializeServices } from '@/services';
import { apiClient } from '@/services/core/apiClient';
import { validateTokens } from '@/utils/jwtUtils';
import { toast } from '@/hooks/use-toast';

interface User {
  id: string;
  email: string;
  fullName: string;
  type: string;
  isActive: boolean;
}

interface LoginResult {
  success: boolean;
  requiresMicrosoftAuth?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  hasuraToken: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeLoginWithToken: (token: string, email: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  isLoading: boolean;
  checkTokenValidity: () => boolean;
}

export const MICROSOFT_PENDING_EMAIL_KEY = 'ms_pending_email';
export const MICROSOFT_PENDING_USER_ID_KEY = 'ms_pending_user_id';

function isMicrosoftAuthSignal(errors?: string[]): boolean {
  return !!errors?.some((e) => e.toLowerCase().includes('different sign-in method'));
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('AuthProvider - Component rendering');
  
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [hasuraToken, setHasuraToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing session on app load
    const initializeAuth = async () => {
      console.log('AuthProvider - Starting auth initialization');
      try {
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');
        const storedHasuraToken = localStorage.getItem('hasuraToken');

        console.log('AuthProvider - Stored tokens check:', {
          hasToken: !!storedToken,
          hasUser: !!storedUser,
          hasHasuraToken: !!storedHasuraToken
        });

        if (storedToken && storedUser && storedHasuraToken) {
          // Validate tokens before restoring session
          const validation = validateTokens(storedToken, storedHasuraToken);

          console.log('AuthProvider - Token validation:', validation);

          if (validation.anyTokenExpired) {
            console.log('AuthProvider - Tokens expired, clearing storage');
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            localStorage.removeItem('hasuraToken');
            setIsLoading(false);
            return;
          }

          const parsedUser = JSON.parse(storedUser);

          // Ensure backward compatibility - add isActive if missing
          if (parsedUser.isActive === undefined) {
            parsedUser.isActive = true; // Default to active for existing sessions
          }

          console.log('AuthProvider - Restoring session for user:', parsedUser.email);

          setToken(storedToken);
          setUser(parsedUser);
          setHasuraToken(storedHasuraToken);

          // Restore user context in Sentry (setUser only fires on fresh login,
          // so a page refresh would otherwise lose the tags for this session)
          setSentryUserContext({
            id: parsedUser.id,
            email: parsedUser.email,
            fullName: parsedUser.fullName,
          });

          // Initialize services with restored auth context
          initializeServices({
            userId: parsedUser.id,
            hasuraToken: storedHasuraToken,
            userType: parsedUser.type
          });
        } else {
          console.log('AuthProvider - No stored session found');
        }
      } catch (error) {
        console.error('AuthProvider - Error during initialization:', error);
        // Clear invalid stored data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('hasuraToken');
      } finally {
        console.log('AuthProvider - Setting isLoading to false');
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Shared by password login and the Microsoft OAuth callback: validates the
  // profile/role/active status, fetches the Hasura token, and establishes the session
  const finishLogin = async (token: string, email: string): Promise<void> => {
    // Get user profile with role and active status validation
    const profileResponse = await usersApi.getProfile(email, token);
    const userProfile = profileResponse.data.profile;

    // Validate authorized roles
    const authorizedRoles = ['EA', 'ES', 'CA', 'CS', 'PA', 'PE', 'ED'];
    if (!authorizedRoles.includes(userProfile.type)) {
      throw new Error('Access denied: Unauthorized role');
    }

    // Check if user is active
    if (!userProfile.isActive) {
      throw new Error('Access denied: Account is inactive');
    }

    // Get Hasura token
    const hasuraResponse = await usersApi.getHasuraToken(userProfile.id, token);
    const hasuraToken = hasuraResponse.data.token;

    // Validate Hasura token structure
    try {
      const tokenParts = hasuraToken.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid Hasura token format');
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      console.log('Hasura token payload:', payload);

      if (!payload['https://hasura.io/jwt/claims']) {
        console.error('Missing Hasura JWT claims in token payload');
        throw new Error('Invalid Hasura token: Missing required JWT claims. Please contact backend team to fix token generation.');
      }
    } catch (decodeError) {
      console.error('Failed to decode Hasura token:', decodeError);
      throw new Error('Invalid Hasura token format. Backend must generate token with https://hasura.io/jwt/claims');
    }

      // Create user object with the correct structure
      const user = {
        id: userProfile.id.toString(),
        email: userProfile.email,
        fullName: userProfile.fullName,
        type: userProfile.type,
        isActive: userProfile.isActive,
      };

      // Set user context in Sentry
      setSentryUserContext({
        id: user.id,
        email: user.email,
        fullName: user.fullName,
      });

    // Set user context in Sentry
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.fullName,
    });

    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('hasuraToken', hasuraToken);
    // Store userId and userEmail for standalone API consumers (e.g. DashboardItemsPage)
    localStorage.setItem('userId', user.id);
    localStorage.setItem('userEmail', user.email);

    setToken(token);
    setUser(user);
    setHasuraToken(hasuraToken);

    // Initialize services with auth context
    initializeServices({
      userId: user.id,
      hasuraToken: hasuraToken,
      userType: user.type
    });

    navigate('/');
  };

  // Redirects the browser to the Microsoft authorize URL, remembering the email
  // so the callback page can finish the session once Microsoft redirects back
  const redirectToMicrosoftLogin = async (email: string, userId?: string): Promise<LoginResult> => {
    localStorage.setItem(MICROSOFT_PENDING_EMAIL_KEY, email);
    if (userId) {
      localStorage.setItem(MICROSOFT_PENDING_USER_ID_KEY, userId);
    }

    const { authorizeUrl } = await authApi.getMicrosoftLoginUrl(email);
    if (!authorizeUrl) {
      throw new Error('Failed to initiate Microsoft authentication. Please try again.');
    }

    window.location.href = authorizeUrl;
    return { success: true, requiresMicrosoftAuth: true };
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      // Sign in to get auth token
      const signinResponse = await authApi.signIn({ email, password });
      const { token } = signinResponse;

      await finishLogin(token, email);

      return { success: true };
    } catch (error: any) {
      // Backend signals an Outlook/Microsoft-managed account by rejecting the
      // password with a "different sign-in method" error instead of a normal 401
      if (isMicrosoftAuthSignal(error?.response?.data?.errors)) {
        return redirectToMicrosoftLogin(email);
      }

      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          section: 'authentication',
          action: 'login'
        },
        extra: {
          email: email,
          errorMessage: error.message
        }
      });

      // Re-throw the error with the properly formatted message from API services
      throw error;
    }
  };

  // Called by the Microsoft OAuth callback page once it has exchanged the
  // authorization code for a token
  const completeLoginWithToken = async (token: string, email: string): Promise<LoginResult> => {
    try {
      await finishLogin(token, email);
      return { success: true };
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: {
          section: 'authentication',
          action: 'microsoft-login'
        },
        extra: {
          email: email,
          errorMessage: error.message
        }
      });
      throw error;
    }
  };

  /**
   * Check if current tokens are valid
   */
  const checkTokenValidity = (): boolean => {
    if (!token || !hasuraToken) {
      return false;
    }

    const validation = validateTokens(token, hasuraToken);
    return validation.allTokensValid;
  };

  const logout = async () => {
    // Step 0: Unlock current patient for ES users before cleanup
    if (user?.type === 'ES' && user?.id && token && hasuraToken) {
      try {
        // Get locked patient from session storage
        const stored = sessionStorage.getItem('es_locked_patient');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.userId === user.id && data.patientId) {
            console.log('ES user logging out - unlocking patient:', data.patientId);
            // Import and use ES service to unlock patient
            const { ESPatientService } = await import('@/services/es/esPatientService');
            const esService = new ESPatientService(user.id, hasuraToken, user.type, token);
            await esService.unlockPatient(data.patientId);
            console.log('Successfully unlocked patient on logout:', data.patientId);
          }
        }
      } catch (error) {
        console.error('Error unlocking patient on logout:', error);
      }
    }

    // Step 1: Stop and cleanup global timer immediately
    try {
      cleanupGlobalTimer();
    } catch (error) {
      // Silent cleanup
    }

    // Step 2: Stop and cleanup patient timer immediately
    try {
      cleanupPatientTimer();
    } catch (error) {
      // Silent cleanup
    }

    // Step 3: Stop and cleanup global notifications immediately
    try {
      cleanupGlobalNotifications();
    } catch (error) {
      // Silent cleanup
    }

    // Step 4: Stop and cleanup HBox call immediately
    try {
      cleanupHBoxCall();
    } catch (error) {
      // Silent cleanup
    }

    // Step 5: Stop and cleanup transfer WebSocket service immediately
    try {
      transferWebSocketService.stopTransferMonitoring();
    } catch (error) {
      // Silent cleanup
    }

    // Step 6: Clear localStorage (auth tokens and inactivity data)
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('hasuraToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');

    // Clear inactivity-related localStorage items
    try {
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('sessionStatus');
      localStorage.removeItem('inactivityWarning');
    } catch (error) {
      // Silent cleanup
    }

    // Clear app-specific localStorage items
    try {
      localStorage.removeItem('hbox_users');
      localStorage.removeItem('app_current_version');
      localStorage.removeItem('isCallActive'); // Clear call state
    } catch (error) {
      // Silent cleanup
    }

    // Step 7: Clear sessionStorage (timer and notification data)
    sessionStorage.removeItem('global_timelog_session');
    sessionStorage.removeItem('patient_timer_session');
    sessionStorage.removeItem('global_notifications_session');
    sessionStorage.removeItem('es_selected_group'); // Clear group selection on logout
    sessionStorage.removeItem('es_locked_patient'); // Clear locked patient info on logout
    sessionStorage.removeItem('navigation_marker');
    
    // Clear global search state and results
    sessionStorage.removeItem('globalSearchState');
    sessionStorage.removeItem('globalSearchResults');

    // Step 8: Reset tab title immediately
    document.title = "HBOX";

    // Step 9: Reset auth state
    setUser(null);
    setToken(null);
    setHasuraToken(null);

    // Clear user context in Sentry
    clearSentryUserContext();

    navigate('/', { replace: true });
  };

  // Automatic HBox call initialization - Triggers on login and page load
  useEffect(() => {
    const initializeHBoxCallForUser = async () => {
      // Only initialize if we have all required auth data and user is fully loaded
      if (user && token && hasuraToken) {
        try {
          await initializeHBoxCallGlobally(user, token, hasuraToken);
        } catch (error) {
          // Silent error handling
        }
      }
    };

    initializeHBoxCallForUser();
  }, [user, token, hasuraToken]); // Triggers on auth state changes - removed isLoading dependency

  // API client 401 handler - Setup session expired handler for profile/hasura endpoints
  useEffect(() => {
    const handleSessionExpired = () => {
      toast({
        title: "Session Expired",
        description: "Your session has expired. Please log in again.",
        variant: "destructive",
        duration: 5000
      });

      logout();
    };

    // Register the session expired handler
    apiClient.setSessionExpiredHandler(handleSessionExpired);

    // Cleanup on unmount
    return () => {
      apiClient.setSessionExpiredHandler(() => { });
    };
  }, [logout]);

  const value = {
    user,
    token,
    hasuraToken,
    login,
    completeLoginWithToken,
    logout,
    isLoading,
    checkTokenValidity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
